import { useState, useEffect, useCallback, Fragment } from 'react';
import { useApiUrl } from '@refinedev/core';
import { getIdToken } from '@/providers/auth-provider';
import { formatDate, truncate } from '@/lib/utils';
import { toast } from '@/hooks/useToast';
import { SortableHeader, nextSort, type SortOrder } from '@/components/table/SortableHeader';
import { FeedbackSender } from './FeedbackSender';
import {
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Save,
  Loader2,
  AlertTriangle,
  Star,
  Smartphone,
  Monitor,
  Bug,
  Flag,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// One row of GET /v1/admin/feedback, which returns `SELECT *` from
// core.beta_feedback: every key here must be a column of that table
// (tests/unit/lint/admin-feedback-entry-matches-columns.test.ts). Until
// 2026-10-16 this type said `email`, `device_os` and `device_platform`; the
// response never had those keys, so the Email column, OS and Platform were
// blank on every row.
interface FeedbackEntry {
  id: string;
  user_id: string | null;
  user_email: string | null;
  feedback_type: 'general' | 'error' | 'milestone' | 'bug';
  feedback_text: string;
  rating: number | null;
  screen_name: string | null;
  status: FeedbackStatus;
  admin_notes: string | null;
  device_model: string | null;
  os_version: string | null;
  platform: string | null;
  app_version: string | null;
  build_number: string | null;
  error_context: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

type FeedbackStatus = 'new' | 'reviewed' | 'in_progress' | 'resolved' | 'wont_fix';
type FeedbackType = 'general' | 'error' | 'milestone' | 'bug';

const STATUS_OPTIONS: { value: FeedbackStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'wont_fix', label: "Won't Fix" },
];

const TYPE_OPTIONS: { value: FeedbackType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'general', label: 'General' },
  { value: 'error', label: 'Error' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'bug', label: 'Bug' },
];

const STATUS_COLORS: Record<FeedbackStatus, string> = {
  new: 'bg-blue-100 text-blue-700',
  reviewed: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-purple-100 text-purple-700',
  resolved: 'bg-green-100 text-green-700',
  wont_fix: 'bg-gray-100 text-gray-600',
};

const TYPE_ICONS: Record<FeedbackType, typeof MessageSquare> = {
  general: MessageSquare,
  error: AlertTriangle,
  milestone: Flag,
  bug: Bug,
};

const PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function RatingStars({ rating }: { rating: number | null }) {
  if (rating === null) return <span className="text-muted-foreground">--</span>;
  return (
    <span className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function FeedbackPage() {
  const apiUrl = useApiUrl();

  // Filters
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<FeedbackType | 'all'>('all');
  const [ratingFilter, setRatingFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Sort
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Pagination
  const [offset, setOffset] = useState(0);

  // Data
  const [entries, setEntries] = useState<FeedbackEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  // Expanded row
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Admin notes editing
  const [editingNotes, setEditingNotes] = useState<Record<string, string>>({});
  const [savingNotes, setSavingNotes] = useState<string | null>(null);

  // Status updating
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  const handleSort = (field: string) => {
    const next = nextSort({ field: sortField, order: sortOrder }, field);
    setSortField(next.field);
    setSortOrder(next.order);
  };

  // ------ Fetch logic ------

  const fetchFeedback = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const token = await getIdToken();
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (typeFilter !== 'all') params.set('feedback_type', typeFilter);
      if (ratingFilter) params.set('rating_min', ratingFilter);
      if (search) params.set('search', search);
      if (sortField) {
        params.set('sort_by', sortField);
        params.set('sort_order', sortOrder);
      }

      const response = await fetch(`${apiUrl}/v1/admin/feedback?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      setEntries(data.feedback ?? []);
      setTotal(data.total ?? 0);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, statusFilter, typeFilter, ratingFilter, search, sortField, sortOrder, offset]);

  const fetchNewCount = useCallback(async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${apiUrl}/v1/admin/feedback?status=new&limit=1&offset=0`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      setNewCount(data.total ?? 0);
    } catch {
      // silent
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  useEffect(() => {
    fetchNewCount();
  }, [fetchNewCount]);

  // Reset offset when filters/sort change
  useEffect(() => {
    setOffset(0);
  }, [statusFilter, typeFilter, ratingFilter, search, sortField, sortOrder]);

  // ------ Actions ------

  const handleStatusChange = async (id: string, newStatus: FeedbackStatus) => {
    setUpdatingStatus(id);
    try {
      const token = await getIdToken();
      const response = await fetch(`${apiUrl}/v1/admin/feedback/${id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      // Update local state
      setEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, status: newStatus } : e))
      );
      await fetchNewCount();

      toast({ title: 'Status Updated', description: `Feedback marked as ${newStatus.replace('_', ' ')}` });
    } catch {
      toast({ title: 'Update Failed', description: 'Could not update status', variant: 'destructive' });
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleSaveNotes = async (id: string) => {
    const notes = editingNotes[id] ?? '';
    setSavingNotes(id);
    try {
      const token = await getIdToken();
      const response = await fetch(`${apiUrl}/v1/admin/feedback/${id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ admin_notes: notes }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      setEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, admin_notes: notes } : e))
      );

      toast({ title: 'Notes Saved', description: 'Admin notes updated successfully' });
    } catch {
      toast({ title: 'Save Failed', description: 'Could not save admin notes', variant: 'destructive' });
    } finally {
      setSavingNotes(null);
    }
  };

  const toggleExpand = (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      // Initialise editing notes from current value
      const entry = entries.find((e) => e.id === id);
      if (entry) {
        setEditingNotes((prev) => ({ ...prev, [id]: entry.admin_notes ?? '' }));
      }
    }
  };

  // ------ Pagination ------

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  // ------ Render ------

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
        Failed to load feedback. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-2xl font-bold">Feedback</h2>
            <p className="text-muted-foreground">User feedback and bug reports from the mobile app</p>
          </div>
          {newCount > 0 && (
            <span className="inline-flex items-center rounded-full bg-blue-600 px-3 py-1 text-sm font-semibold text-white">
              {newCount} new
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as FeedbackStatus | 'all')}
            aria-label="Filter by status"
            className="h-10 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Type:</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as FeedbackType | 'all')}
            aria-label="Filter by type"
            className="h-10 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Min rating:</label>
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
            aria-label="Filter by minimum rating"
            className="h-10 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">Any</option>
            <option value="5">5★</option>
            <option value="4">4★+</option>
            <option value="3">3★+</option>
            <option value="2">2★+</option>
            <option value="1">1★+</option>
          </select>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchInput.trim());
          }}
          className="flex items-center gap-2 sm:ml-auto"
        >
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search email…"
            className="h-10 w-56 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <button
            type="submit"
            className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Search
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="w-8 px-4 py-3" />
                <SortableHeader field="created_at" label="Date" activeField={sortField} activeOrder={sortOrder} onSort={handleSort} />
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Email</th>
                <SortableHeader field="feedback_type" label="Type" activeField={sortField} activeOrder={sortOrder} onSort={handleSort} />
                <SortableHeader field="rating" label="Rating" activeField={sortField} activeOrder={sortOrder} onSort={handleSort} />
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Screen</th>
                <SortableHeader field="status" label="Status" activeField={sortField} activeOrder={sortOrder} onSort={handleSort} />
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Text</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="border-b last:border-0 animate-pulse">
                    <td className="px-4 py-3"><div className="h-4 w-4 rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-28 rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-40 rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-16 rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-20 rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-24 rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-20 rounded bg-muted" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-48 rounded bg-muted" /></td>
                  </tr>
                ))
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                    No feedback found matching the current filters.
                  </td>
                </tr>
              ) : (
                entries.map((entry) => {
                  const isExpanded = expandedId === entry.id;
                  const TypeIcon = TYPE_ICONS[entry.feedback_type] || MessageSquare;

                  return (
                    <Fragment key={entry.id}>
                      {/* Summary row */}
                      <tr
                        className={`border-b last:border-0 cursor-pointer hover:bg-muted/30 ${isExpanded ? 'bg-muted/20' : ''}`}
                        onClick={() => toggleExpand(entry.id)}
                      >
                        <td className="px-4 py-3">
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                          {formatDate(entry.created_at)}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">
                          <FeedbackSender userId={entry.user_id} userEmail={entry.user_email} />
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-sm">
                            <TypeIcon className="h-3.5 w-3.5 text-muted-foreground" />
                            {entry.feedback_type}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <RatingStars rating={entry.rating} />
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {entry.screen_name ?? '--'}
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <select
                            value={entry.status}
                            onChange={(e) =>
                              handleStatusChange(entry.id, e.target.value as FeedbackStatus)
                            }
                            disabled={updatingStatus === entry.id}
                            aria-label="Change status"
                            className={`rounded-full border-0 px-2.5 py-0.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary ${STATUS_COLORS[entry.status]} ${updatingStatus === entry.id ? 'opacity-50' : ''}`}
                          >
                            {STATUS_OPTIONS.filter((o) => o.value !== 'all').map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs">
                          {truncate(entry.feedback_text, 50)}
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {isExpanded && (
                        <tr className="border-b last:border-0 bg-muted/10">
                          <td colSpan={8} className="px-6 py-4">
                            <div className="grid gap-6 md:grid-cols-2">
                              {/* Full text */}
                              <div>
                                <h4 className="mb-2 text-sm font-semibold">Full Feedback</h4>
                                <p className="whitespace-pre-wrap rounded-lg border bg-background p-3 text-sm">
                                  {entry.feedback_text}
                                </p>
                              </div>

                              {/* Device info */}
                              <div>
                                <h4 className="mb-2 text-sm font-semibold flex items-center gap-1.5">
                                  {entry.platform === 'ios' || entry.platform === 'android' ? (
                                    <Smartphone className="h-4 w-4" />
                                  ) : (
                                    <Monitor className="h-4 w-4" />
                                  )}
                                  Device Info
                                </h4>
                                <div className="rounded-lg border bg-background p-3 text-sm space-y-1">
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Model</span>
                                    <span>{entry.device_model ?? '--'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">OS</span>
                                    <span>{entry.os_version ?? '--'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Platform</span>
                                    <span>{entry.platform ?? '--'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">App Version</span>
                                    <span>{entry.app_version ?? '--'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Build</span>
                                    <span>{entry.build_number ?? '--'}</span>
                                  </div>
                                </div>
                              </div>

                              {/* Error context (only if present) */}
                              {entry.error_context && (
                                <div>
                                  <h4 className="mb-2 text-sm font-semibold flex items-center gap-1.5">
                                    <AlertTriangle className="h-4 w-4 text-destructive" />
                                    Error Context
                                  </h4>
                                  <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg border bg-background p-3 text-xs font-mono">
                                    {JSON.stringify(entry.error_context, null, 2)}
                                  </pre>
                                </div>
                              )}

                              {/* Admin notes */}
                              <div>
                                <h4 className="mb-2 text-sm font-semibold">Admin Notes</h4>
                                <textarea
                                  value={editingNotes[entry.id] ?? ''}
                                  onChange={(e) =>
                                    setEditingNotes((prev) => ({
                                      ...prev,
                                      [entry.id]: e.target.value,
                                    }))
                                  }
                                  placeholder="Add internal notes about this feedback..."
                                  rows={3}
                                  className="w-full rounded-lg border bg-background p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-y"
                                />
                                <div className="mt-2 flex justify-end">
                                  <button
                                    onClick={() => handleSaveNotes(entry.id)}
                                    disabled={savingNotes === entry.id}
                                    className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                                  >
                                    {savingNotes === entry.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Save className="h-4 w-4" />
                                    )}
                                    Save Notes
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {total === 0 ? 0 : offset + 1} to{' '}
              {Math.min(offset + PAGE_SIZE, total)} of {total} entries
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                disabled={!hasPrev}
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                title="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>
              <span className="px-2 text-sm">
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
                disabled={!hasNext}
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                title="Next page"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
